import "VSS/LoaderPlugins/Css!fabric";
import "VSS/LoaderPlugins/Css!WorkItemTracking/Components/HtmlEditor";

import * as MentionResources from "Mention/Scripts/Resources/TFS.Resources.Mention";
import { Constants as MentionConstants } from "Mention/Scripts/TFS.Mention";
import * as Social_RichText_Autocomplete_Async from "Mention/Scripts/TFS.Social.RichText.Autocomplete";
import { DirectionalHint } from "OfficeFabric/ContextualMenu";
import { Async, ICancelable } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import * as React from "react";
import * as Rooster_Async from "RoosterReact/rooster-react-amd";
import { getDebugMode } from "VSS/Diag";
import * as Resources_Platform from "VSS/Resources/VSS.Resources.Platform";
import { publishEvent, TelemetryEventData } from "VSS/Telemetry/Services";
import { announce } from "VSS/Utils/Accessibility";
import { Utils } from "VSS/Utils/Html";
import { KeyUtils } from "VSS/Utils/UI";
import * as VSS from "VSS/VSS";
import * as EmojiStrings_Async from "WorkItemTracking/Scripts/Components/HtmlEditor.Emoji.Strings";
import * as Strings_Async from "WorkItemTracking/Scripts/Components/HtmlEditor.Strings";
import * as HtmlEditorLinkPlugin_Async from "WorkItemTracking/Scripts/Components/HtmlEditorLinkPlugin";
import * as HtmlEditorMentionPlugin_Async from "WorkItemTracking/Scripts/Components/HtmlEditorMentionPlugin";
import * as HtmlEditorEscapeCodeBlockPlugin_Async from "WorkItemTracking/Scripts/Components/HtmlEditorEscapeCodeBlockPlugin";
import * as HtmlEditorTabToNextFocusableElementPlugin_Async from "WorkItemTracking/Scripts/Components/HtmlEditorTabToNextFocusableElementPlugin";
import * as WITResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import { closest } from "WorkItemTracking/Scripts/Utils/HtmlUtil";
import { richTextFilterContentAndPreRender } from "WorkItemTracking/Scripts/Utils/RichTextPreRenderUtility";
import { HtmlFieldsMentionsUsageTelemetryHelper } from "WorkItemTracking/Scripts/Components/HtmlFieldsMentionsTelemetry";

const TelemetryAreaName = "HtmlEditor";
const ToolbarButtonClickFeature = "ToolbarButtonClick";
const ShortcutCommandFeature = "ShortcutCommand";
const TelemetryCreateScenario = "CreateHtmlEditor";
const TelemetrySetContentScenario = "SetContent";
const HyperlinkShortcut = KeyUtils.shouldUseMetaKeyInsteadOfControl() ? Resources_Platform.CommandClickToOpen : Resources_Platform.CtrlClickToOpen;

// by default, there is HTML bloat/workaround for Edge and Chrome around lists (only when setting specific font)
// to avoid the bloat, we're disabling this until we allow setting font
const DisableListWorkaround = true;

const PlaceholderImageClassName = "html-editor-img-placeholder";
const ExcludePlaceholderSelector = `:not(.${PlaceholderImageClassName})`;
const SanitizeHtmlOptions = {
    attributeCallbacks: {
        class: (value: string, element: HTMLElement) => {
            const mentionElement = closest(element, `[${MentionConstants.HTML_RENDERED_MENTION_ATTR_NAME}]`);
            return mentionElement ? value : undefined;
        },
        style: (value: string) => value.replace("background-color: rgb(255, 255, 255);", "")
            .replace("color: rgb(0, 0, 0);", ""),
            // Remove white background color and black forecolor
            // to avoid conflicts with dark/custom themes.
            // Stripping of colors should be removed
            // once we have identified the right place to avoid theming conflicts.
            // User Story 1636775: Avoid default color/background-color from being inlined by Rooster Sanitizer        
        [MentionConstants.HTML_MENTION_ATTR_NAME]: v => v,
        [MentionConstants.HTML_RENDERED_MENTION_ATTR_NAME]: v => v
    } as Rooster_Async.AttributeCallbackMap
} as Rooster_Async.SanitizeHtmlOptions;

// we currently don't support file drop
const PreventFileDropHandler = (ev: React.DragEvent<HTMLElement>) => {
    const { dataTransfer } = ev.nativeEvent;
    //The '||' operator below ensures that if dataTransfer is null, the second condition is not checked to avoid a null reference exception
    if (!dataTransfer || !dataTransfer.types) {
        return;
    }

    const fileType = "Files";
    const types = dataTransfer.types as any;
    // IE11 has contains but not indexOf
    if ((types.indexOf && types.indexOf(fileType) >= 0) || (types.contains && types.contains(fileType))) {
        ev.preventDefault();
        try {
            dataTransfer.dropEffect = "none"; // IE11 throws an error
        } catch {}

        return false;
    }
};

const EmojiStringsModuleList = ["WorkItemTracking/Scripts/Components/HtmlEditor.Emoji.Strings"];

const RoosterReactModulePath = "RoosterReact/rooster-react-amd";
const HtmlEditorStringsModulePath = "WorkItemTracking/Scripts/Components/HtmlEditor.Strings";
const HtmlEditorLinkPluginModulePath = "WorkItemTracking/Scripts/Components/HtmlEditorLinkPlugin";
const HtmlEditorMentionPluginModulePath = "WorkItemTracking/Scripts/Components/HtmlEditorMentionPlugin";
const HtmlEditorEscapeCodeBlockPluginModulePath = "WorkItemTracking/Scripts/Components/HtmlEditorEscapeCodeBlockPlugin";
const HtmlEditorTabToNextFocusableElementPluginModulePath = "WorkItemTracking/Scripts/Components/HtmlEditorTabToNextFocusableElementPlugin";
const AutoCompleteModulePath = "Mention/Scripts/TFS.Social.RichText.Autocomplete";
let RoosterReactModule: typeof Rooster_Async = null;
let HtmlEditorStringsModule: typeof Strings_Async = null;
let HtmlEditorLinkPluginModule: typeof HtmlEditorLinkPlugin_Async = null;
let HtmlEditorMentionPluginModule: typeof HtmlEditorMentionPlugin_Async = null;
let HtmlEditorEscapeCodeBlockPluginModule: typeof HtmlEditorEscapeCodeBlockPlugin_Async = null;
let HtmlEditorTabToNextFocusableElementPluginModule: typeof HtmlEditorTabToNextFocusableElementPlugin_Async = null;
let RichTextAutoCompleteModule: typeof Social_RichText_Autocomplete_Async = null;

export interface ITelemetryContext {
    controlName?: string;
}

export interface IHtmlEditorProps {
    className?: string;
    fullScreen?: boolean;
    helpText?: string;
    hidden?: boolean;
    htmlContent: string;
    mentionsEnabled?: boolean;
    mentionsDecorationsEnabled?: boolean;
    onChange?: (content?: string) => void;
    placeholder?: string;
    readonly?: boolean;
    showChromeBorder?: boolean;
    telemetryContext?: ITelemetryContext;
    uploadImageHandler?: (file: File, ignoreAreaPath?: boolean) => Promise<string>;
    ariaLabel?: string;
    height?: number | string;
    forceDelayLoad?: boolean;
    onKeyDown?: (event: KeyboardEvent) => void;
    onFocus?: () => void;
    onBlur?: () => void;

    /** If set, activate the editor on mount */
    activateRoosterOnMount?: boolean;

    onEditorInitialized?: () => void;

    /**
     * If true, the editor is always in edit mode (i.e., LeanRoosterModes.Edit)
     * @default false
     */
    editOnlyMode?: boolean;

    preventShrinkOnBlur?: boolean;

    /**
     * If true, the editor is focussed once component is mounted.
     */
    autoFocus?: boolean;

    /**
     * If true, content is trimmed of whitespace when calling isEmpty().
     */
    trimWithEmptyCheck?: boolean;
}

export interface IHtmlEditorState {
    fullScreen: boolean;
    hidden: boolean;
    moduleLoaded: boolean;
    readonly: boolean;
    defaultHeight: number | string;
}

export class HtmlEditor extends React.PureComponent<IHtmlEditorProps, IHtmlEditorState> {
    private static _nextId: number = 0;
    private _id: number = HtmlEditor._nextId++;
    private _leanRooster: Rooster_Async.LeanRooster;
    private _leanRoosterContentDiv: HTMLDivElement;
    private _commandBar: Rooster_Async.RoosterCommandBar;
    private _commandBarPlugin: Rooster_Async.RoosterCommandBarPlugin;
    private _imageResizePlugin: Rooster_Async.ImageResize;
    private _linkPlugin: HtmlEditorLinkPlugin_Async.HtmlEditorLinkPlugin;
    private _shortcutPlugin: HtmlEditorLinkPlugin_Async.KeyDownPlugin;
    private _async: Async = new Async();
    private _throttledOnContentChanged: (updatedContent: string, isInitializing?: boolean) => void;
    private _editorPlugins: Rooster_Async.EditorPlugin[];
    private _emojiPlugin: Rooster_Async.EmojiPlugin;
    private _undoPlugin: Rooster_Async.UndoWithImagePlugin;
    private _mentionPlugin: HtmlEditorMentionPlugin_Async.HtmlEditorMentionPlugin;
    private _imageManager: Rooster_Async.ImageManager;
    private _buttonOverrides: Rooster_Async.RoosterCommandBarButton[];
    private _autocompleteControl: Social_RichText_Autocomplete_Async.RichTextAutocompleteControl;
    private _autoCompleteSelectedEventName: string;
    private _creationStartTime: number;
    private _asynchronouslyLoaded: boolean;
    private _viewState: Rooster_Async.EditorViewState;
    private _preventBlurForInsertImage: boolean;
    private _coreApiOverride: Partial<Rooster_Async.CoreApiMap>;
    private _htmlEditorRef = React.createRef<HTMLDivElement>();
    private _disposing: boolean;
    private _resetCaret: boolean;

    public constructor(props: IHtmlEditorProps) {
        super(props);

        this._throttledOnContentChanged = this._async.throttle(this._handleContentChanged, 100, { trailing: true });

        let moduleLoaded = false;
        const { htmlContent, readonly, hidden, fullScreen, forceDelayLoad, height, mentionsDecorationsEnabled, mentionsEnabled } = props;
        this._viewState = { content: htmlContent, isDirty: false } as Rooster_Async.EditorViewState;
        this._coreApiOverride = getDebugMode()
            ? ({ triggerEvent: this._triggerEventWithUserTiming } as Partial<Rooster_Async.CoreApiMap>)
            : undefined;

        const initializeRooster = (loadedSynchronously: boolean): void => {
            this._createAdditionalEditorPlugins();
            this._initializeButtonOverrides();
            this._creationStartTime = Date.now();

            moduleLoaded = true;

            const viewState = this._viewState;
            viewState.content = RoosterReactModule.HtmlSanitizer.sanitizeHtml(viewState.content, SanitizeHtmlOptions);
            if (!loadedSynchronously && !this._disposing) {
                this.setState({ moduleLoaded });
            }
        };

        if (
            require.defined(RoosterReactModulePath) &&
            require.defined(HtmlEditorStringsModulePath) &&
            require.defined(HtmlEditorLinkPluginModulePath) &&
            require.defined(HtmlEditorEscapeCodeBlockPluginModulePath) &&
            require.defined(HtmlEditorTabToNextFocusableElementPluginModulePath) &&
            (!mentionsDecorationsEnabled || require.defined(HtmlEditorMentionPluginModulePath)) &&
            (!mentionsEnabled || require.defined(AutoCompleteModulePath))
        ) {
            RoosterReactModule = require(RoosterReactModulePath);
            HtmlEditorStringsModule = require(HtmlEditorStringsModulePath);
            HtmlEditorLinkPluginModule = require(HtmlEditorLinkPluginModulePath);
            HtmlEditorEscapeCodeBlockPluginModule = require(HtmlEditorEscapeCodeBlockPluginModulePath);
            HtmlEditorTabToNextFocusableElementPluginModule = require(HtmlEditorTabToNextFocusableElementPluginModulePath);
            HtmlEditorMentionPluginModule = mentionsDecorationsEnabled ? require(HtmlEditorMentionPluginModulePath) : null;
            RichTextAutoCompleteModule = mentionsEnabled ? require(AutoCompleteModulePath) : null;
            if (forceDelayLoad) {
                setTimeout(() => initializeRooster(false), 0); // this helps exclude rooster initialization from form TTI (like the old editor)
            } else {
                initializeRooster(true);
            }
        } else {
            this._ensureRoosterModule().then(() => initializeRooster(false));
        }

        this.state = { readonly, hidden, fullScreen, moduleLoaded, defaultHeight: height };
    }

    public render(): JSX.Element {
        const { className, editOnlyMode, height = this.state.defaultHeight } = this.props;

        return (
            <div
                className={css("html-editor", className, {
                    "is-hidden": this.state.hidden,
                    "full-screen": this.state.fullScreen,
                    "auto-grow": height == null,
                    "edit-only-mode": editOnlyMode
                })}
                style={{ height }}
                ref={this._htmlEditorRef}
            >
                {this._renderRoosterEditor()}
            </div>
        );
    }

    public componentWillReceiveProps(nextProps: IHtmlEditorProps, nextState: {}) {
        const { readonly } = nextProps;
        if (readonly !== this.props.readonly && readonly !== this.state.readonly) {
            this.setState({ readonly });
        }
    }

    public componentDidMount(): void {
        if (this._leanRooster && this.props.editOnlyMode) {
            this.mode = RoosterReactModule.LeanRoosterModes.Edit;
        }

        if (this.props.autoFocus) {
            this.focus();
        }

        this._endCreateScenario();
    }

    /**
     *
     * @param newContent html content to set.
     * @param unsafe If true, it will not perform sanitization on the html content. It is used in
     * special cases, like Mentions Reply All feature.
     */
    public setContent(newContent: string, unsafe: boolean = false): void {
        const { height, preventShrinkOnBlur } = this.props;

        const startTime = Date.now();
        // If the module is not loaded, just set the state, we will sanitize when the module has been loaded
        const content = RoosterReactModule && !unsafe ? RoosterReactModule.HtmlSanitizer.sanitizeHtml(newContent, SanitizeHtmlOptions) : newContent;
        if (this._tryUpdateState(content) && this._leanRooster) {
            this._leanRooster.reloadContent(true /* triggerContentChangedEvent */, false /* resetUndo */);
            const initialContent = this._leanRooster.getContent();
            this._undoPlugin.reset(initialContent);
        }

        // Renders mentions when moving to different work items in triage view 
        this._renderContentOnReadOnly();

        const autoGrow = height == null;
        if (preventShrinkOnBlur && autoGrow) {
            this.setState({ defaultHeight: null });
        }

        // Publish telemetry only if it is not part of creation scenario, since setContent before creation just updates the state
        if (RoosterReactModule) {
            const contentLength = (this._viewState.content || "").length;
            const elapsedTime = Date.now() - startTime;
            publishEvent(
                new TelemetryEventData(TelemetryAreaName, TelemetrySetContentScenario, {
                    elapsedTime,
                    contentLength
                })
            );
        }
    }

    public get isDirty(): boolean {
        return this._viewState.isDirty;
    }

    public get htmlContent(): string {
        return this._viewState.content;
    }

    public focus(scrollIntoView?: boolean): void {
        if (this._leanRooster) {
            // manually dispatch focus event to trigger rooster onFocus and onFocusNative handlers
            this._leanRoosterContentDiv.dispatchEvent(new Event("focus"));
            this._leanRooster.focus();
            if (scrollIntoView && this._leanRoosterContentDiv) {
                const contentDiv = this._leanRoosterContentDiv as any;
                if (contentDiv.scrollIntoViewIfNeeded) {
                    contentDiv.scrollIntoViewIfNeeded();
                    return;
                }

                this._leanRoosterContentDiv.scrollIntoView();
            }
        }
    }

    public resetScroll(): void {
        if (this._leanRoosterContentDiv && this._leanRoosterContentDiv.firstChild) {
            // resetting scroll immediately will causes focus on control so delay it only when user explicitly focus into the control
            this._resetCaret = true; 
            this._leanRoosterContentDiv.scrollTop = 0;
        }
    }

    public selectAll(): void {
        if (this._leanRooster) {
            this._leanRooster.selectAll();
        }
    }

    public flushChanges(): void {
        if (this._leanRooster) {
            // Force the content changed event bypassing the throttling to ensure that all changes are processed before saving the workitem.
            const content = this._leanRooster.getContent();
            this._handleContentChanged(content);
        }
    }

    // force update on command bar will cause reflow, debounce here for all callers
    public refreshCommandBar: ICancelable<() => void> & (() => void) = this._async.debounce(
        () => this._commandBar && this._commandBar.forceUpdate(),
        100,
        {
            trailing: true
        }
    );

    public hasFocus(): boolean {
        return this._leanRooster && this.mode === RoosterReactModule.LeanRoosterModes.Edit;
    }

    public isEmpty(): boolean {
        return this._leanRooster && this._leanRooster.isEmpty();
    }

    public setEnabled(enabled: boolean): void {
        this.setState({ readonly: !enabled });
    }

    public setVisible(visible: boolean): void {
        this.setState({ hidden: !visible });
    }

    public setFullScreen(fullScreen: boolean): void {
        this.setState({ fullScreen });
    }

    public get mode(): Rooster_Async.LeanRoosterModes {
        return this._leanRooster.mode;
    }

    public set mode(value: Rooster_Async.LeanRoosterModes) {
        this._leanRooster.mode = value;
    }

    public get editableElement(): HTMLDivElement {
        return this._leanRoosterContentDiv;
    }

    public componentWillUnmount(): void {
        this._disposing = true;
        const leanRoosterContentDiv = this._leanRoosterContentDiv;
        if (leanRoosterContentDiv) {
            this._leanRoosterContentDiv = null;
            this._emojiPlugin = null; // set to null to avoid race with _loadEmojiStrings(); also note rooster will call dispose on its plugins

            if (this._autocompleteControl) {
                leanRoosterContentDiv.removeEventListener(this._autoCompleteSelectedEventName, this._onMentionSelect);
                this._autocompleteControl.cleanUpEditor(leanRoosterContentDiv);
                this._autocompleteControl = null;
            }
        }

        if (this._async) {
            this._async.dispose();
            this._async = null;
        }
    }

    private _renderRoosterEditor(): JSX.Element {
        if (!this.state.moduleLoaded) {
            this._asynchronouslyLoaded = true;
            return null;
        }

        const { showChromeBorder, placeholder, ariaLabel, activateRoosterOnMount, trimWithEmptyCheck, mentionsDecorationsEnabled } = this.props;
        const { FocusOutShell, LeanRooster, RoosterCommandBar } = RoosterReactModule;
        const { All, CommandBarLocaleStrings } = HtmlEditorStringsModule;

        this._renderContentOnReadOnly();

        return (
            <FocusOutShell
                className={css("rooster-wrapper", { "with-chrome": showChromeBorder })}
                allowMouseDown={this._focusOutShellAllowMouseDown}
                onBlur={this._focusOutShellOnBlur}
                onFocus={this._focusOutShellOnFocus}
                shouldCallBlur={this._shouldCallBlur}
            >
                {(calloutClassName: string, calloutOnDismiss: (ev: React.FocusEvent<HTMLElement>) => void) => {
                    this._createPluginsWithCallout(calloutClassName, calloutOnDismiss);
                    return [
                        <LeanRooster
                            key="rooster"
                            className={css("rooster-editor", "propagate-keydown-event", "text-element")}
                            viewState={this._viewState}
                            plugins={this._editorPlugins}
                            undo={this._undoPlugin}
                            ref={this._leanRoosterOnRef}
                            updateViewState={this._updateViewState}
                            contentDivRef={this._leanRoosterContentDivOnRef}
                            readonly={this.state.readonly}
                            placeholder={placeholder}
                            hyperlinkToolTipCallback={this._hyperlinkToolTipCallback}
                            onFocus={this._leanRoosterOnFocus}
                            onDragEnter={PreventFileDropHandler}
                            onDragOver={PreventFileDropHandler}
                            aria-label={ariaLabel}
                            activateRoosterOnMount={activateRoosterOnMount}
                            activateInViewMode={mentionsDecorationsEnabled}
                            coreApiOverride={this._coreApiOverride}
                            sanitizeAttributeCallbacks={SanitizeHtmlOptions.attributeCallbacks}
                            trimWithEmptyCheck={trimWithEmptyCheck}
                            onHyperlinkClick={this._onHyperlinkClick}
                            clickOpenHyperlinkViewMode={true}
                        />,
                        <RoosterCommandBar
                            key="cmd"
                            className="rooster-command-bar"
                            commandBarClassName="html-command-bar-base"
                            buttonOverrides={this._buttonOverrides}
                            roosterCommandBarPlugin={this._commandBarPlugin}
                            emojiPlugin={this._emojiPlugin}
                            imageManager={this._imageManager}
                            calloutClassName={css("rooster-callout", calloutClassName)}
                            calloutOnDismiss={calloutOnDismiss}
                            strings={CommandBarLocaleStrings}
                            ref={this._commandBarOnRef}
                            onButtonClicked={this._onButtonClicked}
                            disableListWorkaround={DisableListWorkaround}
                            overflowMenuProps={{ className: "rooster-command-bar-overflow" }}
                            ellipsisAriaLabel={All.Toolbar_More}
                            tooltipDirectionHint={DirectionalHint.bottomCenter}
                        />
                    ];
                }}
            </FocusOutShell>
        );
    }

    private _createPluginsWithCallout(calloutClassName: string, calloutOnDismiss: (ev: React.FocusEvent<HTMLElement>) => void): any {
        const { CommandBarLocaleStrings, SearchForEmoji } = HtmlEditorStringsModule;
        const { RoosterCommandBarPlugin, EmojiPlugin } = RoosterReactModule;

        if (!this._emojiPlugin) {
            this._emojiPlugin = new EmojiPlugin({
                calloutClassName: css(calloutClassName, "rooster-emoji-callout"),
                calloutOnDismiss,
                emojiPaneProps: {
                    quickPickerClassName: "rooster-emoji-quick-pick",
                    navBarProps: {
                        className: "rooster-emoji-navbar",
                        buttonClassName: "rooster-emoji-navbar-button",
                        iconClassName: "rooster-emoji-navbar-icon",
                        selectedButtonClassName: "rooster-emoji-navbar-selected"
                    },
                    statusBarProps: {
                        className: "rooster-emoji-status-bar"
                    },
                    emojiIconProps: {
                        className: "rooster-emoji-icon",
                        selectedClassName: "rooster-emoji-selected"
                    },
                    searchPlaceholder: SearchForEmoji,
                    searchInputAriaLabel: SearchForEmoji
                } as Rooster_Async.EmojiPaneProps,
                onKeyboardTriggered: this._publishEmojiFromKeyboardTelemetry,
                strings: { emjDNoSuggetions: WITResources.NoSuggestionsFound } //  only load a small portion of required strings first (the rest is async loaded)
            } as Rooster_Async.EmojiPluginOptions);
            this._editorPlugins.push(this._emojiPlugin);
        }

        if (!this._commandBarPlugin) {
            this._commandBarPlugin = new RoosterCommandBarPlugin({
                strings: CommandBarLocaleStrings,
                onShortcutTriggered: this._publishShortcutCommandTelemetry,
                disableListWorkaround: DisableListWorkaround,
                calloutClassName,
                calloutOnDismiss
            } as Rooster_Async.RoosterCommandBarPluginOptions);
            this._editorPlugins.push(this._commandBarPlugin);
        }
    }

    private _ensureRoosterModule(): Promise<void> {
        const { mentionsDecorationsEnabled, mentionsEnabled } = this.props;

        return new Promise<void>(resolve => {
            if (
                RoosterReactModule &&
                HtmlEditorStringsModule &&
                HtmlEditorLinkPluginModule &&
                HtmlEditorEscapeCodeBlockPluginModule &&
                HtmlEditorTabToNextFocusableElementPluginModule &&
                (!mentionsDecorationsEnabled || HtmlEditorMentionPluginModule) &&
                (!mentionsEnabled || RichTextAutoCompleteModule)
            ) {
                resolve();
                return;
            }

            const modulePaths = [
                RoosterReactModulePath,
                HtmlEditorStringsModulePath,
                HtmlEditorLinkPluginModulePath,
                HtmlEditorEscapeCodeBlockPluginModulePath,
                HtmlEditorTabToNextFocusableElementPluginModulePath
            ];

            if (mentionsDecorationsEnabled) {
                modulePaths.push(HtmlEditorMentionPluginModulePath);
            }

            if (mentionsEnabled) {
                modulePaths.push(AutoCompleteModulePath);
            }

            VSS.using(
                modulePaths,
                (
                    RoosterReact: typeof Rooster_Async,
                    HtmlEditorStrings: typeof HtmlEditorStringsModule,
                    HtmlEditorLinkPlugin: typeof HtmlEditorLinkPluginModule,
                    HtmlEscapeCodeBlockPlugin: typeof HtmlEditorEscapeCodeBlockPluginModule,
                    HtmlTabToNextFocusableElementPlugin: typeof HtmlEditorTabToNextFocusableElementPluginModule,
                    module0: any,
                    module1: any
                ): void => {
                    RoosterReactModule = RoosterReact;
                    HtmlEditorStringsModule = HtmlEditorStrings;
                    HtmlEditorLinkPluginModule = HtmlEditorLinkPlugin;
                    HtmlEditorEscapeCodeBlockPluginModule = HtmlEscapeCodeBlockPlugin;
                    HtmlEditorTabToNextFocusableElementPluginModule = HtmlTabToNextFocusableElementPlugin;

                    // The optional plugins can come in different orders depending on which ones are enabled
                    if (mentionsDecorationsEnabled) {
                        HtmlEditorMentionPluginModule = HtmlEditorMentionPluginModule || module0;

                        if (mentionsEnabled) {
                            RichTextAutoCompleteModule = RichTextAutoCompleteModule || module1;
                        }
                    }
                    else if (mentionsEnabled) {
                        RichTextAutoCompleteModule = RichTextAutoCompleteModule || module0;
                    }
                    resolve();
                },
                VSS.handleError
            );
        });
    }

    private _initializeButtonOverrides(): void {
        const { All } = HtmlEditorStringsModule;

        const supportInsertImage = !!this.props.uploadImageHandler;
        const keys = RoosterReactModule.RoosterCommmandBarButtonKeys;
        const buttonClassName = "html-command-bar-button";
        this._buttonOverrides = [
            { key: keys.Bold, order: 0 },
            { key: keys.Italic, order: 1 },
            { key: keys.Underline, order: 2 },
            { key: keys.BulletedList, order: 3 },
            { key: keys.NumberedList, order: 4 },
            { key: keys.Highlight, order: 5, subMenuPropsOverride: { ariaLabel: All.HighlightColorPicker_AriaLabel } },
            { key: keys.FontColor, order: 6, subMenuPropsOverride: { ariaLabel: All.FontColorPicker_AriaLabel } },
            { key: keys.Emoji, order: 7, buttonClassName: css("rooster-emoji-button", buttonClassName) },
            { key: keys.Outdent, order: 8 },
            { key: keys.Indent, order: 9 },
            { key: keys.Strikethrough, order: 10 },
            { key: keys.Header, order: 11, subMenuPropsOverride: { ariaLabel: All.HeaderMenu_AriaLabel } },
            { key: keys.Code, order: 12 },
            { key: keys.ClearFormat, order: 16 },
            { key: keys.InsertImage, order: 17, exclude: !supportInsertImage },
            { key: keys.Link, order: 18 },
            { key: keys.Unlink, order: 19 }
        ];

        if (this.props.mentionsEnabled) {
            this._buttonOverrides.push({
                key: HtmlEditorStringsModule.CustomButtonKeys.AtMention,
                name: MentionResources.MentionSomeone,
                iconProps: { iconName: "Accounts" },
                onRender: RoosterReactModule.getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("@"),
                order: 13
            });
            this._buttonOverrides.push({
                key: HtmlEditorStringsModule.CustomButtonKeys.HashMention,
                name: MentionResources.MentionWorkItem,
                iconProps: { iconName: "NumberSymbol" },
                onRender: RoosterReactModule.getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("#"),
                order: 14
            });
            this._buttonOverrides.push({
                key: HtmlEditorStringsModule.CustomButtonKeys.PRMention,
                name: MentionResources.MentionPR,
                iconProps: { iconName: "BranchPullRequest" },
                onRender: RoosterReactModule.getIconOnRenderDelegate(),
                handleChange: this._createMentionButttonHandler("!"),
                order: 15
            });
        }

        this._buttonOverrides.forEach(b => {
            b.className = b.className || "html-command-button-root";
            b.buttonClassName = b.buttonClassName || buttonClassName;
        });
    }

    private _createMentionButttonHandler(mentionChar: string): (editor: Rooster_Async.Editor) => void {
        return (editor: Rooster_Async.Editor): void => {
            this._autocompleteControl.closeActiveAutocomplete();

            const selection = editor.getSelection();
            const alreadyHasWhitespace = selection && selection.focusNode && /\s/.test(selection.focusNode.textContent.substr(-1));
            editor.insertContent(alreadyHasWhitespace ? mentionChar : ` ${mentionChar}`);
            editor.triggerContentChangedEvent("Mentions");

            this._autocompleteControl.suggest(this._leanRoosterContentDiv);
        };
    }

    private _createAdditionalEditorPlugins(): void {
        const { uploadImageHandler: uploadImage, onKeyDown, mentionsDecorationsEnabled } = this.props;
        const {
            ImageManager,
            UndoWithImagePlugin,
            ContentChangedPlugin,
            ImageResize,
            TableResize,
            PasteImagePlugin,
            DoubleClickImagePlugin,
            IgnorePasteImagePlugin
        } = RoosterReactModule;

        this._imageManager = new ImageManager({
            uploadImage,
            placeholderImageClassName: PlaceholderImageClassName,
            fallbackAltValue: HtmlEditorStringsModule.All.ImageDefaultAltText
        } as Rooster_Async.ImageManagerOptions);
        this._undoPlugin = new UndoWithImagePlugin(this._imageManager);
        this._imageResizePlugin = new ImageResize(undefined, undefined, undefined, undefined, ExcludePlaceholderSelector);
        this._linkPlugin = new HtmlEditorLinkPluginModule.HtmlEditorLinkPlugin();

        const supportInsertImage: boolean = !!uploadImage;
        this._editorPlugins = [
            new ContentChangedPlugin(this._throttledOnContentChanged),
            this._imageResizePlugin,
            new TableResize(),
            supportInsertImage ? new PasteImagePlugin(this._imageManager) : IgnorePasteImagePlugin.Instance,
            new DoubleClickImagePlugin(ExcludePlaceholderSelector),
            this._linkPlugin,
            new HtmlEditorEscapeCodeBlockPluginModule.HtmlEditorEscapeCodeBlockPlugin(),
            new HtmlEditorTabToNextFocusableElementPluginModule.HtmlEditorTabToNextFocusableElementPlugin()
        ];

        if (mentionsDecorationsEnabled) {
            this._mentionPlugin = new HtmlEditorMentionPlugin_Async.HtmlEditorMentionPlugin();
            this._editorPlugins.push(this._mentionPlugin);
        }

        if (onKeyDown) {
            this._shortcutPlugin = new HtmlEditorLinkPlugin_Async.KeyDownPlugin(onKeyDown);
            this._editorPlugins.push(this._shortcutPlugin);
        }
    }

    private _tryUpdateState(newContent: string, isInitializing: boolean = false): boolean {
        newContent = Utils.isEmpty(newContent) ? "" : newContent;
        const viewState = this._viewState;

        if (newContent !== viewState.content) {
            viewState.content = newContent;
            viewState.isDirty = true;

            return !isInitializing;
        }

        return false;
    }

    private _renderContentOnReadOnly(): void {
        if (this.state.readonly && this.props.mentionsDecorationsEnabled) {
            const contentDiv = document.createElement("div");
            contentDiv.innerHTML = this._viewState.content;
            richTextFilterContentAndPreRender(contentDiv, false).then(renderedContent => {
                if (this._leanRoosterContentDiv) {
                    this._leanRoosterContentDiv.innerHTML = renderedContent;
                }
            }).catch(e => HtmlFieldsMentionsUsageTelemetryHelper.publishMentionRenderFail(e));
        }
    }

    private _endCreateScenario() {
        if (this._creationStartTime) {
            const contentLength = (this._viewState.content || "").length;
            const asyncLoaded = !!this._asynchronouslyLoaded;
            const elapsedTime = Date.now() - this._creationStartTime;
            publishEvent(
                new TelemetryEventData(TelemetryAreaName, TelemetryCreateScenario, {
                    elapsedTime,
                    contentLength,
                    asyncLoaded
                })
            );
            this._creationStartTime = null;
        }
    }

    private _publishEmojiFromKeyboardTelemetry = (): void => {
        this._publishShortcutCommandTelemetry("Emoji");
    };

    private _onButtonClicked = (command: string): void => {
        if (command == RoosterReactModule.RoosterCommmandBarButtonKeys.InsertImage) {
            this._preventBlurForInsertImage = true;
        }

        this._publishToolbarTelemetry(command);
    };

    private _publishToolbarTelemetry(command: string): void {
        const controlName = (this.props.telemetryContext || ({} as ITelemetryContext)).controlName;
        publishEvent(
            new TelemetryEventData(TelemetryAreaName, ToolbarButtonClickFeature, {
                controlName,
                command
            })
        );
    }

    private _publishShortcutCommandTelemetry = (command: Rooster_Async.RoosterShortcutCommands | string): void => {
        const controlName = (this.props.telemetryContext || ({} as ITelemetryContext)).controlName;
        publishEvent(
            new TelemetryEventData(TelemetryAreaName, ShortcutCommandFeature, {
                controlName,
                command
            })
        );
    };

    private _handleContentChanged = (content: string, isInitializing: boolean = false): void => {
        if (this._tryUpdateState(content, isInitializing) && this.props.onChange) {
            this.props.onChange(this.isEmpty() ? "" : content);
        }
    };

    private _updateViewState = (existingViewState: Rooster_Async.EditorViewState, content: string, isInitializing: boolean): void => {
        this._throttledOnContentChanged(content, isInitializing);
    };

    private _focusOutShellAllowMouseDown = (element: HTMLElement): boolean => {
        const isContenteditable = this._leanRoosterContentDiv && this._leanRoosterContentDiv.contains(element);

        if (!isContenteditable && this._autocompleteControl) {
            // make sure autocomplete is closed when click on a non-content-editable element
            this._autocompleteControl.closeActiveAutocomplete();
        }

        return isContenteditable;
    };

    private _focusOutShellOnFocus = (ev: React.FocusEvent<HTMLElement>): void => {
        const { height, onFocus, editOnlyMode, preventShrinkOnBlur } = this.props;

        this._commandBarPlugin.registerRoosterCommandBar(this._commandBar); // re-register command b/c we're changing mode on blur
        if (this._leanRooster && !editOnlyMode) {
            this.mode = RoosterReactModule.LeanRoosterModes.Edit;

            const autoGrow = height == null;
            if (preventShrinkOnBlur && autoGrow) {
                this.setState({ defaultHeight: null });
            }
        }

        onFocus && onFocus();
    };

    private _leanRoosterOnFocus = (ev: React.FocusEvent<HTMLElement>): void => {
        const { helpText, mentionsDecorationsEnabled } = this.props;
        if (helpText && this._leanRooster && this._leanRooster.hasPlaceholder()) {
            announce(helpText, true);
        }

        if (mentionsDecorationsEnabled && !this.state.readonly) {
            this._mentionPlugin.onFocus();
        }
        
        if (this._resetCaret) {
            this._resetCaret = false;
            const firstChild = this._leanRoosterContentDiv.firstChild;
            if (firstChild) {
                const selection = window.getSelection();
                const range = document.createRange();
                range.setStart(firstChild, 0);
                range.setEnd(firstChild, 0);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    };

    private _onHyperlinkClick = (anchor: HTMLAnchorElement, ev: MouseEvent): boolean => {
        const href = anchor.getAttribute("href");
        if (this._leanRooster.mode !== RoosterReactModule.LeanRoosterModes.View || !href) {
            return false; // let the built-in Hyperlink plugin handle edit mode
        }

        // intercept hyperlink click event only for view mode
        this._linkPlugin.translateUrl(href, result => {
            try {
                window.open(result, "_blank");
            } catch {}
        });

        return true;
    };

    private _focusOutShellOnBlur = (ev: React.FocusEvent<HTMLElement>): void => {
        const { height, onBlur, editOnlyMode, preventShrinkOnBlur, mentionsDecorationsEnabled } = this.props;

        if (this._leanRooster && !editOnlyMode) {
            this.mode = RoosterReactModule.LeanRoosterModes.View;
            this._imageResizePlugin.hideResizeHandle();

            const autoGrow = height == null;
            const rootDiv = this._htmlEditorRef.current;
            if (preventShrinkOnBlur && autoGrow && rootDiv) {
                const height = rootDiv.getBoundingClientRect().height;

                // A height of 0 means that the tab has switched and the root div is no longer visible.
                // This can occur when an @mention popup is present, then switching
                // to the 'history' tab (for example)
                if (height > 0) {
                    this.setState({ defaultHeight: height });
                }
            }
        }

        onBlur && onBlur();

        if (mentionsDecorationsEnabled && !this.state.readonly) {
            this._mentionPlugin.onBlur();
        }
    };

    private _shouldCallBlur = (nextTarget: HTMLElement, shouldCallBlurDefault: (nextTarget: HTMLElement) => boolean): boolean => {
        if (this._preventBlurForInsertImage) {
            this._preventBlurForInsertImage = false;
            return false;
        }

        return shouldCallBlurDefault(nextTarget);
    };

    private _leanRoosterOnRef = (ref: Rooster_Async.LeanRooster): void => {
        this._leanRooster = ref;
        this._endCreateScenario();

        const { onEditorInitialized } = this.props;
        if (onEditorInitialized) {
            onEditorInitialized();
        }
    };

    private _leanRoosterContentDivOnRef = (ref: HTMLDivElement): void => {
        this._leanRoosterContentDiv = ref;
        this.props.mentionsEnabled && !this.state.readonly && this._setupAutocomplete();
        this._loadEmojiStrings();
    };

    private _commandBarOnRef = (ref: Rooster_Async.RoosterCommandBar): void => {
        this._commandBar = ref;
    };

    private _hyperlinkToolTipCallback = (href: string): string => {
        // roosterjs will support which link to process in the future, for now we don't show tooltip
        // href having just hash (@ mention)
        if (href.replace(location.href, "") === "#") {
            return "";
        }

        if (this.hasFocus()) {
            return `${href}\n${HyperlinkShortcut}`;
        }

        return null;
    };

    private _onMentionSelect = (): void => this._throttledOnContentChanged(this._leanRooster.getContent());

    private _setupAutocomplete(): void {
        const { mentionsEnabled } = this.props;

        if (this._leanRoosterContentDiv && RichTextAutoCompleteModule && mentionsEnabled) {
            this._autocompleteControl = RichTextAutoCompleteModule.RichTextAutocompleteControl.getInstance();
            this._autocompleteControl.attachToEditable(this._leanRoosterContentDiv);
            this._autoCompleteSelectedEventName = RichTextAutoCompleteModule.RichTextAutocompleteControl.SELECTED_EVENT_NAME;
            this._leanRoosterContentDiv.addEventListener(this._autoCompleteSelectedEventName, this._onMentionSelect);
        }
    }

    private _loadEmojiStrings(): void {
        VSS.using(
            EmojiStringsModuleList,
            (EmojiStrings: typeof EmojiStrings_Async): void => this._emojiPlugin && this._emojiPlugin.setStrings(EmojiStrings.EmojiLocaleStrings)
        );
    }

    private _triggerEventWithUserTiming = (core: Rooster_Async.EditorCore, pluginEvent: Rooster_Async.PluginEvent, broadcast: boolean) => {
        const eventType = pluginEvent.eventType;
        const perf = core.document.defaultView.performance;
        const controlName = (this.props.telemetryContext || ({} as ITelemetryContext)).controlName || "";
        const id = `${controlName}_${this._id}`;

        measure(`TOTAL_TIME_EVENT${eventType}`, id, perf, () => {
            if (broadcast || !core.plugins.some(plugin => handledEventExclusivelyWithMeasure(pluginEvent, id, plugin, perf))) {
                core.plugins.forEach(plugin => {
                    const pluginName = plugin.getName() || plugin.constructor.name;
                    if (plugin.onPluginEvent) {
                        measure(`${pluginName}_EVENT${eventType}`, id, perf, () => plugin.onPluginEvent(pluginEvent));
                    }
                });
            }
        });
    };
}

function handledEventExclusivelyWithMeasure(
    event: Rooster_Async.PluginEvent,
    id: string,
    plugin: Rooster_Async.EditorPlugin,
    perf: Performance
): boolean {
    if (!plugin.onPluginEvent || !plugin.willHandleEventExclusively) {
        return false;
    }

    const pluginName = plugin.getName() || plugin.constructor.name;
    const eventType = event.eventType;
    let result: boolean;
    measure(`${pluginName}_EVENT${eventType}_EXCLUSIVE`, id, perf, () => {
        result = plugin.willHandleEventExclusively(event);
    });
    result && measure(`${pluginName}_EVENT${eventType}`, id, perf, () => plugin.onPluginEvent(event));

    return result;
}

function measure(name: string, id: string, perf: Performance, callback: () => void) {
    if (!perf || !perf.mark) {
        return callback();
    }

    // use bare bone user timing to help debug performance with minimal impact on interaction experience as user types
    const fullName = `${name}_${id}`;
    const start = `${fullName}_S`;
    const end = `${fullName}_E`;

    perf.mark(start);
    callback();
    perf.mark(end);
    try {
        perf.measure(fullName, start, end);
        perf.clearMarks(start);
        perf.clearMarks(end);
    } catch {}
}
